/**
 * EthicSport customization
 * es-tabs-scroll.js
 *
 * 1. Scrolls the active tab button into the center of the scroll container
 *    when a tab is clicked. Only runs when the tab strip is in scroll mode
 *    (i.e. its scrollWidth exceeds its clientWidth — mobile/narrow viewports).
 *
 * 2. Auto-expands the sticky ATC bar content when it becomes visible,
 *    mirroring the "open by default" behaviour from the old-new reference.
 */

(function () {
  "use strict";

  /**
   * Smoothly scroll the tab strip so that the active button is centered.
   * @param {HTMLElement} strip  - the .tabbed-content--tabs element
   * @param {HTMLElement} button - the button that was just activated
   */
  function scrollTabIntoCenter(strip, button) {
    // Only act when the strip is actually overflowing (scroll mode)
    if (strip.scrollWidth <= strip.clientWidth) return;

    const stripCenter = strip.clientWidth / 2;
    const buttonCenter = button.offsetLeft + button.offsetWidth / 2;
    const targetScrollLeft = buttonCenter - stripCenter;

    strip.scrollTo({
      left: targetScrollLeft,
      behavior: "smooth",
    });
  }

  /**
   * Attach click listeners to every button inside a tabbed-content element.
   * @param {HTMLElement} tabbedContent - the <tabbed-content> custom element
   */
  function attachToTabbedContent(tabbedContent) {
    const strip = tabbedContent.querySelector(".tabbed-content--tabs");
    if (!strip) return;

    const buttons = Array.from(strip.querySelectorAll("button"));
    buttons.forEach(function (button) {
      button.addEventListener("click", function () {
        // Let the theme's own click handler run first, then scroll
        requestAnimationFrame(function () {
          scrollTabIntoCenter(strip, button);
        });
      });
    });
  }

  /**
   * Initialise all tabbed-content elements currently in the DOM.
   */
  function init() {
    document.querySelectorAll("tabbed-content").forEach(attachToTabbedContent);
  }

  // Wait for the custom element to be defined and upgraded before attaching,
  // so the theme's own connectedCallback has already run.
  if (customElements && typeof customElements.whenDefined === "function") {
    customElements.whenDefined("tabbed-content").then(init);
  } else {
    // Fallback for older browsers
    document.addEventListener("DOMContentLoaded", init);
  }

  // Also handle dynamically injected tabbed-content blocks (Shopify theme editor)
  document.addEventListener("shopify:section:load", function (event) {
    var section = event.target;
    if (section) {
      section.querySelectorAll("tabbed-content").forEach(attachToTabbedContent);
    }
  });

  // ─── Sticky ATC: auto-expand when bar slides into view ───────────────────

  /**
   * Open the sticky ATC content panel immediately when the bar becomes visible,
   * so the user sees the ATC button without needing an extra click.
   * The toggle still works normally for closing/re-opening.
   *
   * The theme adds/removes `.sticky--visible` via IntersectionObserver in
   * product.js. We watch for that class change with a MutationObserver.
   */
  function initStickyATCAutoExpand() {
    var bar = document.querySelector("product-add-to-cart-sticky");
    if (!bar) return;

    var content = bar.querySelector(".product-add-to-cart-sticky--content");
    var inner = bar.querySelector(".product-add-to-cart-sticky--inner");
    if (!content || !inner) return;

    function expand() {
      // If the custom element has a GSAP timeline, use it to animate open
      if (bar.tl && typeof bar.tl.play === "function") {
        if (bar.tl.reversed()) {
          bar.tl.play();
        }
      } else {
        // Fallback for non-animated path (animations setting disabled)
        if (!content.classList.contains("active")) {
          content.classList.add("active");
          inner.classList.add("sticky-open");
        }
      }
    }

    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.attributeName === "class") {
          if (bar.classList.contains("sticky--visible")) {
            // Small delay so the slide-in transition starts first
            setTimeout(expand, 50);
          }
        }
      });
    });

    mo.observe(bar, { attributes: true, attributeFilter: ["class"] });
  }

  // Run after custom elements are ready
  if (customElements && typeof customElements.whenDefined === "function") {
    customElements.whenDefined("product-add-to-cart-sticky").then(initStickyATCAutoExpand);
  } else {
    document.addEventListener("DOMContentLoaded", initStickyATCAutoExpand);
  }
})();
